import { CloseOutlined } from '@ant-design/icons';
import AppButton from '@nxsys/components/AppButton';
import AppDatePicker from '@nxsys/components/AppDatePicker';
import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppModalContainer from '@nxsys/components/AppModalContainer';
import AppPayFrequencySelect from '@nxsys/components/AppPayFrequencySelect';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { Form } from 'antd';
import { FormProps } from 'antd/lib';
import moment, { Moment } from 'moment';
import React from 'react';
import AppRateInput from '@nxsys/components/AppRateInput';

type DormantWorkerReportFilterProps = {
  isOpen: boolean;
  toggle: () => void;
  onFilter: (params: DormantWorkerReportFilterValues) => void;
};

export type DormantWorkerReportFilterValues = {
  payFrequency: string;
  dormantWeek: number;
  // Inclusive last-paid date range (YYYY-MM-DD).
  lastPaidFrom?: string;
  lastPaidTo?: string;
};

type DormantWorkerReportFilterForm = {
  payFrequency: string;
  dormantWeek: number;
  lastPaidFrom?: Moment | null;
  lastPaidTo?: Moment | null;
};

const DormantWorkerReportFilterModal: React.FC<
  DormantWorkerReportFilterProps
> = ({ isOpen, toggle, onFilter }: DormantWorkerReportFilterProps) => {
  const { messages } = useTypedIntl();

  const [form] = Form.useForm();
  const watchValue = Form.useWatch([], form);
  const ableToClearFilter =
    watchValue && Object.keys(watchValue).some((key) => !!watchValue[key]);

  const handleClearAll = (e: React.MouseEvent) => {
    e.stopPropagation();
    form.resetFields();
  };

  const onFinish: FormProps<DormantWorkerReportFilterForm>['onFinish'] = (
    values,
  ) => {
    onFilter({
      payFrequency: values.payFrequency,
      dormantWeek: values.dormantWeek,
      lastPaidFrom: values.lastPaidFrom
        ? moment(values.lastPaidFrom).format('YYYY-MM-DD')
        : undefined,
      lastPaidTo: values.lastPaidTo
        ? moment(values.lastPaidTo).format('YYYY-MM-DD')
        : undefined,
    });
    toggle();
  };

  const onFinishFailed: FormProps['onFinishFailed'] = (errorInfo) => {
    console.log('Failed:', errorInfo);
  };

  return (
    <AppModalContainer
      title={messages['common.filter']}
      isOpen={isOpen}
      toggle={toggle}
      actions={[
        {
          name: messages['common.cancel'],
          variant: 'outlined',
          onClick: toggle,
        },
        {
          name: messages['common.apply'],
          buttonType: 'primary',
          onClick: () => form.submit(),
        },
      ]}
      closeIcon={
        <div style={{ marginRight: 90 }}>
          <AppButton
            name={messages['common.clearAll']}
            leftIcon={<CloseOutlined />}
            onClick={handleClearAll as any}
            disabled={!ableToClearFilter}
          />
        </div>
      }
      minHeight="200px"
    >
      <AppFormContainer
        form={form}
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        layout="vertical"
        labelCol={{ span: 24 }}
        wrapperCol={{ span: 24 }}
        initialValues={{
          dormantWeek: 4,
        }}
      >
        <Form.Item label={messages['common.payFrequency']} name="payFrequency">
          <AppPayFrequencySelect
            placeholder={messages['common.pleaseSelect']}
            onChange={(value) => {
              form.setFieldsValue({ payFrequency: value });
            }}
          />
        </Form.Item>
        <Form.Item label={messages['common.dormantWeek']} name="dormantWeek">
          <AppRateInput
            placeholder={messages['common.pleaseSelect']}
          />
        </Form.Item>
        <div className="flex gap-x-4">
          <Form.Item
            label={`${messages['common.lastPaid']} ${messages['common.from']}`}
            name="lastPaidFrom"
            className="w-1/2"
          >
            <AppDatePicker
              placeholder={messages['common.selectDate']}
              className="!w-full"
              format="DD/MM/YYYY"
            />
          </Form.Item>
          <Form.Item
            label={`${messages['common.lastPaid']} ${messages['common.to']}`}
            name="lastPaidTo"
            className="w-1/2"
          >
            <AppDatePicker
              placeholder={messages['common.selectDate']}
              className="!w-full"
              format="DD/MM/YYYY"
              disabledDate={(current: Moment) =>
                !!watchValue?.lastPaidFrom &&
                current.isBefore(
                  moment(watchValue.lastPaidFrom).startOf('day'),
                  'day',
                )
              }
            />
          </Form.Item>
        </div>
      </AppFormContainer>
    </AppModalContainer>
  );
};

export default DormantWorkerReportFilterModal;
